import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import InfoIcon from "../../assets/icons/info-01.svg2";
import { getHelp } from "./getHelp";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setInformationSidepanel } from "../store/slices/informationSidepanelSlice";
import { useGetHelpContentQuery } from "../store/slices/knowledgeBaseSlice";
import Showdown from "showdown";
import { isHelpButtonsVisible } from "../store/slices/featuresSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

type HelpKey =
  | "settings-billing-details"
  | "settings-accounts-contacts"
  | "billing-overview-products"
  | "billing-overview-account-bolt-ons"
  | "broadcast-email"
  | "broadcast-sms"
  | "broadcast-voice"
  | "broadcast-whatsapp"
  | "broadcast-messenger"
  | "broadcast-rcs"
  | "broadcast-comms-selector"
  | "broadcast-comms-types"
  | "broadcast-dynamic-header"
  | "broadcast-email-subject"
  | "broadcast-email-from"
  | "broadcast-override-email-defaults"
  | "broadcast-email-sender"
  | "broadcast-redirect-message"
  | "broadcast-voice-welcome-message"
  | "broadcast-main-message"
  | "broadcast-message"
  | "broadcast-voice-exit-message"
  | "broadcast-reply-handlers"
  | "broadcast-status-handlers"
  | "broadcast-method"
  | "broadcast-name"
  | "broadcast-description"
  | "broadcast-reference"
  | "broadcast-1-way"
  | "broadcast-2-way"
  | "project-originator"
  | "project-budget"
  | "project-pertinent-option"
  | "components-business-hours"
  | "components-calendars"
  | "components-data-categories"
  | "components-input-arguments"
  | "components-message-configs"
  | "components-message-templates"
  | "components-participant-categories"
  | "components-add-listener"
  | "components-events"
  | "components-configurations"
  | "components-contacts-and-groups"
  | "components-actions"
  | "components-conditions"
  | "widget-active-users"
  | "widget-map"
  | "widget-messages-by-statuses"
  | "widget-messages-by-comms-type"
  | "widget-system-usage"
  | "widgets-monitoring"
  | "widget-solutions"
  | "contact-set-runtime-filters"
  | "reporting-overview"
  | "reporting-templates"
  | "api-profile-options-auth-header"
  | "api-profile-options-timeout"
  | "api-profile-options-default-method"
  | "api-connection-advanced-query-parameters"
  | "api-connection-advanced-post-form-parameters"
  | "api-connection-advanced-headers"
  | "api-connection-advanced-authentication"
  | "api-connection-about"
  | "api-connection-label"
  | "api-connection-description"
  | "api-connection-reference"
  | "api-connection-status"
  | "api-data-structures"
  | "api-data-structures-edit"
  | "imported-contacts-files-library"
  | "select-project-interface"
  | "variables"
  | "modules"
  | "select-project-interface"
  | "api-overview-about-this-api"
  | "api-overview-title"
  | "api-overview-description"
  | "api-overview-reference"
  | "api-overview-status"
  | "api-example-request-headers"
  | "inbound-campaign-category"
  | "inbound-campaign-number"
  | "contact_fields"
  | "create-broadcast-overview"
  | "create-api-connection-overview"
  | "create-boomlogic-project-overview"
  | "create-campaign-overview"
  | "inbound-campaign-form-overview"
  | "contact-communication-fields"
  | "contact-card-fields"
  | "contact-other-fields"
  | "contact-location"
  | "contact-groups"
  | "assets-overview"
  | "maps"
  | "events"
  | "view-manage-edit-events"
  | "monitor-priority"
  | "monitor-ping-status"
  | "monitor-status"
  | "ticketing-overview"
  | "ticketing-data"
  | "realtime-reporting"
  | "realtime-messaging-data"
  | string;

// id of the doc, arr of intro elems, arr of the more elems, bool include intro in more, source link to click _blank
export default function HelpButton({
  helpKey,
  btnClassName,
  iconClassName,
}: {
  btnClassName?: string;
  iconClassName?: string;
  helpKey: HelpKey;
}) {
  const isVisible = useSelector(isHelpButtonsVisible);
  const [show, setShow] = useState(false);
  const dispatch = useDispatch();
  const { data } = useGetHelpContentQuery(helpKey, {
    skip: !show || !helpKey || !isVisible,
  });

  const classMap: any = {
    ul: "list-group",
    ol: "list-group",
    li: "list-group-item",
    table: "table",
  };

  const bindings = Object.keys(classMap).map((key) => {
    return {
      type: "output",
      regex: new RegExp(`<${key}(.*)>`, "g"),
      replace: `<${key} class="${classMap[key]}" $1>`,
    };
  });

  const converter = new Showdown.Converter({
    extensions: [
      ...bindings,
      {
        type: "output",
        regex: `<img src="`,
        replace: `<img class="w-100" src="https://help.uatboomcomms.com`,
      },
    ],
  });
  const introHTML = converter.makeHtml(data?.intro ?? "");
  const moreHTML = converter.makeHtml(data?.more ?? "");

  const popover = (
    <Popover
      style={{ minWidth: 300, maxWidth: 800 }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Popover.Header className="bg-colored-reverse text-white ">
        <div className="d-flex align-items-center list">
          <div className="d-flex align-items-center fw-bold">
            <InfoIcon width={15} className="me-2 text-white icon-bold" /> #
            {helpKey}
          </div>
          <div className="flex-grow-1" />{" "}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShow(false);
            }}
            className={`btn btn-link p-0 border-0 ms-1 mb-1 text-white`}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </Popover.Header>
      <Popover.Body className="bg-page">
        {introHTML ? (
          <div
            className={`mb-2 `}
            dangerouslySetInnerHTML={{ __html: introHTML }}
          />
        ) : (
          "No help content available."
        )}

        {data && data.more && (
          <div>
            <Button
              type="button"
              variant="link"
              className="p-0"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                dispatch(
                  setInformationSidepanel({
                    id: helpKey,
                    sourceLink: data.slug,
                    // elements: moreContent,
                    content: introHTML + moreHTML,
                  })
                );
              }}
            >
              more
            </Button>
          </div>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    isVisible && (
      <span
      // onClick={(e) => {
      //   if (preventRootClose) {
      //     e.stopPropagation();
      //   }
      // }}
      >
        <OverlayTrigger
          placement="auto"
          overlay={popover}
          trigger="click"
          rootClose
          show={show}
          onToggle={(nextShow) => setShow(nextShow)}
          delay={{ show: 300, hide: 100 }}
        >
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShow(true);
            }}
            className={`btn btn-link p-0 border-0 ms-1 opacity-50 mb-1 ${btnClassName}`}
          >
            <InfoIcon width={15} className={iconClassName} />
          </span>
        </OverlayTrigger>
      </span>
    )
  );
}

export function HelpContent({ helpKey }: { helpKey: HelpKey }) {
  const isVisible = useSelector(isHelpButtonsVisible);
  const [show, setShow] = useState(false);
  const dispatch = useDispatch();
  const { data } = useGetHelpContentQuery(helpKey, {
    skip: !helpKey,
  });

  const classMap: any = {
    ul: "list-group",
    ol: "list-group",
    li: "list-group-item",
    table: "table",
  };

  const bindings = Object.keys(classMap).map((key) => {
    return {
      type: "output",
      regex: new RegExp(`<${key}(.*)>`, "g"),
      replace: `<${key} class="${classMap[key]}" $1>`,
    };
  });

  const converter = new Showdown.Converter({
    extensions: [
      ...bindings,
      {
        type: "output",
        regex: `<img src="`,
        replace: `<img class="w-100" src="https://help.uatboomcomms.com`,
      },
    ],
  });
  const introHTML = converter.makeHtml(
    data?.intro.split("\n").slice(1).join(" ") ?? ""
  );
  const moreHTML = converter.makeHtml(data?.more ?? "");

  return (
    <>
      <div
        className={`mb-2 `}
        dangerouslySetInnerHTML={{ __html: introHTML }}
      />

      {data && data.more && (
        <div>
          <Button
            type="button"
            variant="link"
            className="p-0"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              dispatch(
                setInformationSidepanel({
                  id: helpKey,
                  sourceLink: data.slug,
                  // elements: moreContent,
                  content: introHTML + moreHTML,
                })
              );
            }}
          >
            more
          </Button>
        </div>
      )}
    </>
  );
}
